import React, {useEffect, useState, createContext, useContext, useReducer} from "react";
import firebase from "firebase/app";
import * as Sentry from "@sentry/browser";
import Promise from "bluebird";
import Cookies from "js-cookie";

const UserContext = createContext({});

const API_URL = "https://internal.hackernoon.com/auth";

const POSTAUTH_FAILURE_MSG = "The cookie exchange failed, signing in anonymously...";

const initialState = {uid: null, noAuth: true};

const reducer = (state, action) => {
  switch (action.type) {
    case "reset":
      return initialState;
    case "set":
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
};

const fetchMongoProfile = async (uid, token) => {
  const profileApiUrl = "https://internal.hackernoon.com/usercontext";
  // const profileApiUrl = "http://localhost:8087/profiles";

  try {
    const res = await fetch(`${profileApiUrl}?uid=${uid}`, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json();
    if (!res.ok || !data.ok || !data.body) {
      throw new Error("Mongo profile not found or malformed");
    }
    return data.body;
  } catch (err) {
    console.error("Error fetching Mongo profile:", err);
    return null;
  }
};

const UserContextProvider = ({children}) => {
  let [user, userDispatch] = useReducer(reducer, initialState);
  // const [loadingAuthState, setLoadingAuthState] = useState(true);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);

  const setUser = (keyOrObj, val) => {
    if (keyOrObj && val) {
      userDispatch({type: "set", payload: {[keyOrObj]: val}});
    } else if (keyOrObj && typeof keyOrObj === "object") {
      userDispatch({type: "set", payload: keyOrObj});
    } else {
      throw new Error("Invalid parameters for setUser");
    }
  };

  const setUserData = async authUser => {
    if (!authUser?.uid || authUser.id) return;
    const id = authUser.uid || authUser.id;
    const token = await authUser.getIdToken(true);
    const profileUser = await fetchMongoProfile(id, token);

    await fetch(`${API_URL}`, {
      method: "POST",
      credentials: "include",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({idToken: token}),
    });

    const profileDocData = profileUser || {};
    const unreadBell = !!profileUser?.notificationsRead === false;
    const avatar = profileDocData.avatar ?? authUser.photoURL ?? `https://hackernoon.com/avatars/${authUser.uid}.png`;
    const userData = {
      ...profileDocData,
      unreadBell,
      avatar,
      uid: authUser.uid || profileDocData.id,
      noAuth: false,
    };

    const shouldOnBoard = !(!!userData?.displayName && !!userData?.handle);
    if (shouldOnBoard) {
      setIsOnboarding(shouldOnBoard);
      userData.onBoardingComplete = false;
    }

    setUser(userData);

    setLoading(false);
  };

  const logout = async () => {
    await fetch(API_URL + "/logout", {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    await firebase
      .auth()
      .signOut()
      .then(() => userDispatch({type: "reset"}));

    userDispatch({type: "reset"});
  };

  const tryPostAuth = async () => {
    try {
      const hasAuthCookie = Cookies.get("hasAuthCookie", {domain: ".hackernoon.com"});
      if (!hasAuthCookie) {
        setLoading(false);
        return;
      }

      const postAuthRes = await fetch(`${API_URL}/postauth`, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: {"Content-Type": "application/json"},
      });

      if (!postAuthRes.ok) throw new Error(POSTAUTH_FAILURE_MSG);
      const {token} = await postAuthRes.json();
      if (!token) throw new Error(POSTAUTH_FAILURE_MSG);

      // if (!firebase) {
      //   firebase = (await import("firebase/app"));
      // }

      const result = await firebase.auth().signInWithCustomToken(token);
      const authUser = result?.user;

      if (authUser?.uid) {
        await setUserData(authUser);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error("tryPostAuth error:", err);
      userDispatch({type: "reset"});
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      // firebase = (await import("firebase/app"));
      firebase.auth().onAuthStateChanged(async authUser => {
        if (authUser) {
          try {
            await setUserData(authUser);
          } catch (err) {
            console.error("Error setting user data:", err);
            setLoading(false);
          }
        } else {
          await tryPostAuth(); // this will handle setting loading
        }
      });
    })();
  }, []);

  const updateCurrentUserProfile = async (body, userId = null) => {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) throw new Error("No authenticated Firebase user — please refresh and try again.");

    // const url = new URL("http://localhost:8087/profiles/update");
    const url = new URL("https://us-central1-hackernoon-app.cloudfunctions.net/profilesApi/update");
    if (userId) {
      url.searchParams.append("userId", userId);
    } else {
      url.searchParams.append("userId", user?.uid);
    }
    const token = await currentUser.getIdToken();

    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    return await res.json();
  };

  const updateUserPreferences = async (body, userId = null) => {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) throw new Error("No authenticated Firebase user — please refresh and try again.");

    // const url = new URL("http://localhost:8087/preferences");
    const url = new URL("https://us-central1-hackernoon-app.cloudfunctions.net/hackernoonMongo2/preferences");

    if (userId) {
      url.searchParams.append("userId", userId);
    } else {
      url.searchParams.append("userId", user?.uid);
    }

    const token = await currentUser.getIdToken();
    try {
      const res = await fetch(url, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      return await res.json();
    } catch (error) {
      console.error("Error updating preferences:", error);
      throw error;
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        authenticated: !!user?.uid && !!user?.email,
        getCurrentUser: () => firebase.auth().currentUser,
        userDispatch,
        setUserData,
        loadingAuthState: loading,
        isOnboarding,
        setIsOnboarding,
        updateCurrentUserProfile,
        updateUserPreferences,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

let UserContextConsumer = UserContext.Consumer;

const useUser = () => useContext(UserContext);

const authenticate = async idToken => {
  await fetch(`${API_URL}`, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({idToken: idToken}),
  });
};

export {authenticate, UserContext, UserContextProvider, UserContextConsumer, useUser};
